import React, { useState, useEffect, useRef } from "react";
import { Tag, Search, Check, ChevronDown } from "lucide-react";
import { DashboardStats } from "./DashboardStats";
import { SalesHistory } from "./SalesHistory";
import { SalesChart } from "./SalesChart";
import { MonthSelector } from "./MonthSelector";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/lib/supabase";
import {
  formatCurrency,
  calculateCommission,
  getCommissionPercentage,
  getTierName,
  isTopPerformerRate,
} from "@/lib/commission";
import type { CouponAnalysis } from "@/types/coupon";

interface DashboardHomeProps {
  userCoupon: string;
  couponAnalysis: CouponAnalysis | null;
  couponLoading: boolean;
  lastUpdated: string | null;
  dateRange: { from: Date | undefined; to: Date | undefined };
  onMonthSelect: (year: number, month: number) => void;
  onNavigateToWallet: () => void;
  availableBalance: number | null;
  isAdmin?: boolean;
  onCouponChange?: (coupon: string) => void;
}

interface CouponOption {
  coupon: string;
  name: string;
}

export const DashboardHome: React.FC<DashboardHomeProps> = ({
  userCoupon,
  couponAnalysis,
  couponLoading,
  lastUpdated,
  dateRange,
  onMonthSelect,
  onNavigateToWallet,
  availableBalance,
  isAdmin,
  onCouponChange,
}) => {
  const [allCoupons, setAllCoupons] = useState<CouponOption[]>([]);
  const [couponSearch, setCouponSearch] = useState("");
  const [couponPopoverOpen, setCouponPopoverOpen] = useState(false);
  const couponSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchCoupons = async () => {
      const { data } = await supabase
        .from("influencers")
        .select("coupon, name")
        .not("coupon", "is", null)
        .order("name");
      if (data) {
        setAllCoupons(
          data
            .filter((d) => d.coupon)
            .map((d) => ({ coupon: d.coupon!, name: d.name || d.coupon! }))
        );
      }
    };
    fetchCoupons();
  }, [isAdmin]);

  useEffect(() => {
    if (couponPopoverOpen) {
      setTimeout(() => couponSearchRef.current?.focus(), 0);
    } else {
      setCouponSearch("");
    }
  }, [couponPopoverOpen]);

  const filteredCoupons = allCoupons.filter((c) => {
    const q = couponSearch.toLowerCase();
    return c.coupon.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
  });
  return (
    <>
      {/* Coupon Analysis Headline */}
      {userCoupon && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            {isAdmin && onCouponChange && allCoupons.length > 0 ? (
              <Popover open={couponPopoverOpen} onOpenChange={setCouponPopoverOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 border-transparent bg-transparent hover:bg-white/10 transition-colors px-1 rounded-full cursor-pointer">
                    <Tag className="h-6 w-6 text-primary" />
                    <span className="font-bold tracking-widest text-foreground uppercase text-xl">
                      {userCoupon.toUpperCase()}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <div className="flex items-center gap-2 border-b px-3 py-2">
                    <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                    <input
                      ref={couponSearchRef}
                      type="text"
                      placeholder="Buscar cupom..."
                      value={couponSearch}
                      onChange={(e) => setCouponSearch(e.target.value)}
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto p-1">
                    {filteredCoupons.length === 0 ? (
                      <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        Nenhum cupom encontrado
                      </div>
                    ) : (
                      filteredCoupons.map((c) => (
                        <button
                          key={c.coupon}
                          onClick={() => {
                            onCouponChange(c.coupon);
                            setCouponPopoverOpen(false);
                          }}
                          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                        >
                          {c.coupon === userCoupon ? (
                            <Check className="h-4 w-4 shrink-0" />
                          ) : (
                            <span className="w-4 shrink-0" />
                          )}
                          <span className="font-mono font-bold uppercase">{c.coupon.toUpperCase()}</span>
                          <span className="text-muted-foreground text-xs truncate">{c.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <>
                <Tag className="h-6 w-6 text-primary" />
                <span className="font-bold tracking-widest text-foreground uppercase text-xl">{userCoupon.toUpperCase()}</span>
              </>
            )}
            <MonthSelector dateRange={dateRange} onMonthSelect={onMonthSelect} />
            {lastUpdated && (
              <span className="text-xs text-muted-foreground ml-auto">
                Atualizado: {new Date(lastUpdated).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>
      )}

      <DashboardStats
        couponAnalysis={couponAnalysis}
        couponLoading={couponLoading}
        formatCurrency={formatCurrency}
        calculateCommission={calculateCommission}
        getCommissionPercentage={getCommissionPercentage}
        getTierName={getTierName}
        isTopPerformerRate={isTopPerformerRate}
        onNavigateToWallet={onNavigateToWallet}
        availableBalance={availableBalance}
      />

      {/* Sales History and Performance Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 mb-8">
        <SalesHistory couponAnalysis={couponAnalysis} loading={couponLoading} />
        <SalesChart couponAnalysis={couponAnalysis} dateRange={dateRange} loading={couponLoading} />
      </div>
    </>
  );
};
